import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 執事の時間・予定管理指針（route=/standards/schedule）=====
// デザイン土台: 紺#1a2942 / 真鍮金#a38e65 / BTN-08 / 紺帯番号バンド / 白リッチカード / 細罫エディトリアル行
// /butler/become や /standards/crisis と同型セクションの使い回しはしない（構成を変えた新バリエーション）
// 事実正典: 創立1890年（明治23年）/ 法人設立2025年2月19日 / 代表理事 石井裕一（三代目頭取）/
//   資格は初級Associate・中級Professional・上級Masterの3段階+専門資格
// 創作した年号・数値・実績・具体的なツール名や製品名は書かない

export default function StandardsSchedulePage() {
  useEffect(() => {
    updateSEO(seoData['/standards/schedule']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '基準・指針', href: '/standards' },
    { label: '執事の時間・予定管理指針' }
  ];

  const BTN08_ON_LIGHT =
    "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY =
    "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const foresightLayers = [
    {
      en: 'Dawn',
      ja: '朝の立ち上がり',
      text: '起床から最初の外出までの動線、身支度の順序、朝食と服薬のタイミングを、依頼者のリズムに合わせて静かに先読みします。急かさず、遅れも生まない余白を朝のうちに確保します。'
    },
    {
      en: 'Day',
      ja: '日中の接続',
      text: '会合・移動・会食・在宅の作業がどう連なるかを一本の線で見ます。空白に見える時間帯にも、準備・待機・短い休息が潜んでいないかを確認します。'
    },
    {
      en: 'Dusk',
      ja: '夕刻の収束',
      text: '帰宅後の来客、家族との時間、翌朝への持ち越しを切り分けます。一日の終わりに未決の手配が残らないよう、夕方の時点で翌日の骨子を整えます。'
    },
    {
      en: 'Night',
      ja: '夜の静けさ',
      text: '就寝前に翌日の確定事項と未確定事項を分け、当番への申し送りを短く残します。夜間の緊急連絡だけが通る状態を保ち、不要な通知で眠りを割らない配慮も予定管理に含めます。'
    }
  ];

  const bufferAxes = [
    {
      no: '01',
      title: '移動の性質',
      text: '徒歩・車・公共交通の別に加え、荷物の有無、同行者、到着後すぐ会合があるかを見ます。到着そのものではなく、到着後に落ち着いて入れる余白までを移動時間に含めます。'
    },
    {
      no: '02',
      title: '天候の読み',
      text: '雨・風・気温の変化は、着替え・傘・車両の手配・玄関の動線に影響します。予報を断定せず、悪化した場合の代替動線をあらかじめ一案持っておきます。'
    },
    {
      no: '03',
      title: '渋滞と混雑',
      text: '時間帯による道路と駅の混雑、行事や工事の影響を、出発前に短く確認します。遅れが見えた時点で次の予定の相手へ先に一報を入れる判断も、余裕の一部です。'
    },
    {
      no: '04',
      title: '到着後の準備',
      text: '会場の入館、受付、席次、資料の手渡しなど、本編の前に必ず発生する手順を忘れません。移動がちょうど良くても、直前準備が詰まれば全体が急ぎ足になります。'
    }
  ];

  const changeRebuild = [
    {
      label: '変更の受け方',
      lines: [
        '誰から・いつ・何が変わったかを、短い事実としてまず書き留める',
        '依頼者本人の意向か、代理からの伝達かを混同しない',
        '確定か仮置きかを明示し、関係者へ同じ言葉で共有する'
      ]
    },
    {
      label: '影響の洗い出し',
      lines: [
        '前後の予定、移動、手配先、家族・来客のどれが連鎖するかを列挙する',
        'キャンセル・延期・短縮・場所変更の別に、必要な連絡先を分ける',
        '当日中に決着すべきことと、翌日以降でよいことを切り分ける'
      ]
    },
    {
      label: '再構成の手順',
      lines: [
        '最優先の予定を軸に、動かせる予定から順に組み直す',
        '手配先へは謝罪より先に、新しい時刻・場所・人数を正確に伝える',
        '再構成後の一日を依頼者へ短く確認し、承認を得てから確定とする'
      ]
    }
  ];

  const familyGuestPoints = [
    {
      en: 'Overlap',
      ja: '重なりの検知',
      text: '家族の外出、来客の到着、依頼者本人の会合が同じ時間帯に重ならないかを、週単位と当日単位の両方で突合します。名前と目的が似た予定ほど、取り違えに注意します。'
    },
    {
      en: 'Priority',
      ja: '優先の合意',
      text: 'どちらを先にするかは、執事が独断で決めません。家ごとの価値観と、その日の体調・公式性を踏まえ、確認すべき相手へ静かに問い直します。'
    },
    {
      en: 'Space',
      ja: '空間の割り当て',
      text: '応接・食堂・駐車場・通用口など、人が同時に使う場所の空きを予定表に含めます。時間は空いていても、空間が塞がっていれば成立しない予定があります。'
    },
    {
      en: 'Tone',
      ja: '場のトーン',
      text: '公式な来客の直後に家族だけの時間を置くなど、空気の切り替えが必要な並びでは、短い緩衝を挟みます。予定は時刻の羅列ではなく、場の連続でもあります。'
    }
  ];

  const vendorContact = [
    {
      en: 'Brief',
      ja: '依頼の型',
      text: '日時・場所・人数・目的・持参物・禁止事項を、先方の担当が読み返せる短い文にまとめます。口頭だけの依頼は、後から解釈が割れやすいため避けます。'
    },
    {
      en: 'Confirm',
      ja: '確認の往復',
      text: '手配を出したあと、受領の返事と、実施前の再確認をセットにします。返事がない状態を「たぶん大丈夫」と読まないことが、当日の欠落を防ぎます。'
    },
    {
      en: 'Change',
      ja: '変更の伝え方',
      text: '時刻や人数が動いたときは、変更点だけを抜き出して先に伝え、続けて最新の全体像を添えます。旧情報が残ったまま現場に届くことを最も警戒します。'
    }
  ];

  const restGuides = [
    '当番と非番の境界を曖昧にせず、連絡が届く範囲をあらかじめ決めておく',
    '連続した長時間の立ち仕事のあとに、短い休息を予定そのものへ組み込む',
    '自分の通院や家庭の用事も、差し支えのない範囲でチームに共有する',
    '疲れが判断を鈍らせていると感じたら、申し送りを早めて交代を相談する',
    '休息中も緊急連絡だけは通す場合、何が緊急かの定義を共有しておく'
  ];

  const faqItems = [
    {
      q: '予定管理で、執事が先に決めてよい範囲はどこまでですか？',
      a: '移動の余裕、手配の下調べ、家族・来客との突合、変更時の影響洗い出しなど、準備と整理は先に進めて構いません。一方で、予定の可否そのもの、公式性の高い会合の優先順位、個人的な約束の受諾や辞退は、依頼者または定められた代理の意向を確認してから確定します。'
    },
    {
      q: '天候や交通の見込みが外れたとき、どこから連絡を始めるべきですか？',
      a: 'まず依頼者（または当日の同行責任者）へ、遅れの見込みと取り得る選択肢を短く伝えます。そのうえで、次の会合の相手、車両や会場などの手配先へ、新しい到着見込みを共有します。推測で確定時刻を告げず、幅を持った見込みと、次に連絡する時点を添えると先方も動きやすくなります。'
    },
    {
      q: '自分自身の休息は、どのように予定へ入れればよいですか？',
      a: '奉仕の品質は、判断力と観察の持続に支えられます。非番の明示、交代の申し送り、緊急時のみ連絡を受ける範囲の合意を、チームと家の取り決めとして持っておくことが基本です。休息を我慢の美徳にせず、翌日の精度を守るための予定管理の一部として扱います。'
    }
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '執事の時間・予定管理指針｜全日本執事協会が定める基準',
    description:
      '全日本執事協会が定める執事の時間・予定管理指針。基準化の理由、一日の先読み、移動と天候の余裕、変更時の再構成、家族と来客の突合、手配先との連絡、記録と引き継ぎ、自身の休息管理。',
    author: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    publisher: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://japanbutlerassociation.com/standards/schedule'
    }
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ホーム', item: 'https://japanbutlerassociation.com/' },
      { '@type': 'ListItem', position: 2, name: '基準・指針', item: 'https://japanbutlerassociation.com/standards' },
      { '@type': 'ListItem', position: 3, name: '執事の時間・予定管理指針', item: 'https://japanbutlerassociation.com/standards/schedule' }
    ]
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* ヒーロー — 全幅ビルボード */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-std-schedule-01.jpg"
          alt="邸内で一日の流れを静かに見渡し、予定を整える執事"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">
              Schedule Standards
            </span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事の時間・予定管理指針</span>
              <span className="block text-[#d7b779]">先読みと再構成の基準</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              一般社団法人 全日本執事協会は、1890年（明治23年）の創立以来、日本における執事文化の継承に取り組んできました。代表理事・石井裕一（三代目頭取）のもと、本指針は認定団体である当協会が定める時間と予定の基準として、一日の先読み、移動と天候の余裕、変更時の再構成、家族と来客の突合、手配先との連絡、記録と引き継ぎ、自身の休息を示します。予定管理は時計の管理ではなく、依頼者の一日が乱れないための静かな設計です。
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 space-y-20 sm:space-y-24">

          {/* S1. 基準化の理由 — 上帯リード＋二段本文 */}
          <section>
            <div className="border-t-2 border-[#1a2942] pt-8">
              <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                  <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
                    I. Why Standards
                  </span>
                  <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em]">
                    予定管理を<span className="text-[#a38e65]">基準化する理由</span>
                  </h2>
                </div>
                <span className="font-['Playfair_Display',serif] text-[48px] leading-none text-[#a38e65]/30 hidden sm:block">01</span>
              </div>
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                  <p>
                    予定は、紙の上では整って見えても、現場では天候・体調・来客・交通が同時に動きます。個人の記憶力や気転だけに頼ると、同じ家のなかでも当番ごとに余裕の取り方や連絡の速さがばらつきます。基準化の目的は、厳格な時間割を押しつけることではなく、誰が担っても同じ座標で先読みと再構成ができることにあります。
                  </p>
                  <p>
                    当協会は、Associate・Professional・Masterの三段階と専門資格を通じて観察と調整の力を育てると同時に、現場で迷わない予定管理の骨子を本指針にまとめています。日々の振る舞いの节度は
                    <a href="/standards/conduct" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">執事の行動規範</a>
                    と、もてなしの流れは
                    <a href="/standards/hospitality" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">ホスピタリティの基準</a>
                    と併せて参照し、基準全体の見取り図は
                    <a href="/standards" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">基準・指針</a>
                    から俯瞰できます。
                  </p>
                </div>
                <div className="bg-[#fbfaf7] border border-[#e2ddd2] p-7 sm:p-8">
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#333] leading-[1.95] mb-5">
                    予定管理は、特別な行事の前日だけに立ち上がる技能ではありません。朝の立ち上がり、日中の接続、夕刻の収束、夜の申し送り——日常の単調な確認が、そのまま変更に強い一日をつくります。
                  </p>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#333] leading-[1.95]">
                    本指針は「何を先に読むか」「どこに余白を残すか」「変わったときに何から組み直すか」を共有語として示し、依頼者の時間が静かに続くための共通言語を定めます。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* S2. 一日の先読み — 四枚横カード＋下に画像帯 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              II. Day Foresight
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              依頼者の一日を先読みする組み立て
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              先読みとは、予定表を埋めることではありません。依頼者の体調、公式と私的の切り替え、家族の動き、邸内の空間が、一日のなかでどう連なるかを静かに見通すことです。朝・日中・夕刻・夜の四層で骨子を持つと、途中の変更にも戻りどころができます。
            </p>
            <div className="grid sm:grid-cols-2 gap-0 border border-[#e2ddd2] mb-8">
              {foresightLayers.map((item) => (
                <div key={item.en} className="px-6 sm:px-7 py-6 border-b sm:border-r border-[#e2ddd2] odd:sm:border-r even:sm:border-r-0 [&:nth-last-child(-n+2)]:sm:border-b-0 last:border-b-0">
                  <span className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em] block mb-2">{item.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{item.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-center">
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                先読みの精度は、詳細な分刻みより、切り替え点の把握に現れます。外出の直前、来客の到着前、会食のあと、就寝前——空気と動線が変わる瞬間に、何を手元へ出し、何を下げ、誰へ一声かけるかを決めておきます。Master Butlerが示す統括も、この切り替えをチーム全体の呼吸へ揃えるところにあります。
              </p>
              <img
                src="/images/generated/jba-std-schedule-02.jpg"
                alt="一日の流れと役割分担を共有するチームのブリーフィング"
                className="w-full h-52 object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S3. 移動と天候 — 紺帯見出し＋四段グリッド */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              III. Travel & Weather
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              移動と天候の余裕をどう見るか
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              余裕は、ただ早く出発することではありません。移動の性質、天候、混雑、到着後の準備——四つの軸で「遅れが生まれやすい地点」を先に見つけ、そこに静かな余白を置くことです。余白は怠慢ではなく、依頼者が慌てずに場へ入るための設計です。
            </p>
            <div className="bg-[#1a2942] px-6 sm:px-8 py-4 mb-0 flex flex-wrap items-center gap-x-8 gap-y-2">
              <span className="font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.18em] text-sm">ROUTE · WEATHER · CROWD · ARRIVAL</span>
              <span className="font-['Noto_Serif_JP',serif] text-[#f8f6f0]/85 text-sm tracking-[0.06em]">
                余白は速さのためではなく、品位のため
              </span>
            </div>
            <div className="grid sm:grid-cols-2 border border-t-0 border-[#e2ddd2] bg-white mb-8">
              {bufferAxes.map((item) => (
                <div key={item.no} className="px-6 sm:px-8 py-6 border-b sm:border-r border-[#e2ddd2] odd:sm:border-r even:sm:border-r-0">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl block mb-2">{item.no}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{item.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-[300px_1fr] gap-8 items-center">
              <img
                src="/images/generated/jba-std-schedule-03.jpg"
                alt="出発前に移動と天候の余白を点検する執事"
                className="w-full h-48 lg:h-52 object-cover border border-[#a38e65]/30"
              />
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                予報や道路の状況は、断定の材料ではなく選択肢を増やす材料です。「晴れるはず」と決め打ちするより、「雨なら玄関の動線をこう変える」と一案持っておく方が、当日の声のトーンは低く保たれます。余裕を数字の分だけ足すのではなく、依頼者が次の場で最初の言葉を急がずに済む状態をつくる——それが本指針の見る余裕です。
              </p>
            </div>
          </section>

          {/* S4. 変更時の再構成 — 左引用＋三列カード */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              IV. Rebuild on Change
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              予定の変更が入ったときの再構成
            </h2>
            <div className="bg-[#fbfaf7] border-l-2 border-[#a38e65] px-6 sm:px-8 py-6 mb-8">
              <p className="font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#333] leading-[1.95]">
                変更は失敗ではありません。むしろ、予定管理の本領は変更の瞬間に現れます。焦って最初に手を動かす前に、何が変わったのかを事実として固定し、影響の輪を洗い、軸となる予定から組み直す——この三段を飛ばさないことが、二次的な混乱を防ぎます。
              </p>
            </div>
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              {changeRebuild.map((block) => (
                <div key={block.label} className="bg-white border border-[#e2ddd2] shadow-[0_12px_40px_rgba(26,41,66,0.06)] p-7">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-4 pb-4 border-b border-[#e2ddd2]">{block.label}</h3>
                  <ul className="space-y-3">
                    {block.lines.map((line) => (
                      <li key={line} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[14px] text-[#333] leading-[1.85]">
                        <span className="text-[#a38e65] shrink-0 mt-0.5">▸</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <img
              src="/images/gen2/std2-schedule-rebuild-training.jpg"
              alt="移動計画と予定カードを並べ、変更時の再構成を確認する研修"
              className="w-full h-56 sm:h-64 object-cover border border-[#a38e65]/30"
            />
          </section>

          {/* S5. 家族と来客の突合 — 二列（本文＋縦リスト） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              V. Family & Guests
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              家族と来客の予定の突合
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              依頼者本人の予定だけを見ていては、邸宅の一日は成立しません。家族の外出、子どもの送り迎え、来客の到着と見送り、使用する部屋と駐車場——複数の人の時間が同じ屋根の下で交差します。突合とは、カレンダーを重ねて矛盾を見つける作業であり、同時に場のトーンを守る作業です。
            </p>
            <div className="grid md:grid-cols-2 gap-0 border-t-2 border-[#1a2942] mb-8">
              {familyGuestPoints.map((r) => (
                <div key={r.en} className="border-b md:border-r odd:md:border-r even:md:border-r-0 border-[#e2ddd2] px-5 sm:px-6 py-6">
                  <span className="block font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.12em] mb-2">{r.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{r.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{r.text}</p>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-[1fr_300px] gap-8 items-start">
              <div className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] space-y-[18px]">
                <p>
                  突合で見つかった重なりは、すぐに片方を消す判断へ進みません。どちらが公式性が高いか、どちらが延期しにくいか、空間が同時に使えるか——確認すべき相手へ、選択肢を短く添えて問い直します。執事の役割は裁定者になることではなく、判断材料を整えることです。
                </p>
                <p>
                  来客の予定には、到着時刻だけでなく、滞在の長さ、食事の要否、退出後の原状回復まで含めます。家族の予定には、プライバシーを侵さない範囲で、共用部の使用だけを共有する節度も必要です。情報の扱いと予定の突合は、常に一体で進めます。
                </p>
              </div>
              <img
                src="/images/gen2/std2-schedule-reconciliation.jpg"
                alt="複数の予定表を照合し、家族と来客の時間を調整する執事"
                className="w-full h-52 object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S6. 手配先との連絡 — 三分割トップ＋補足 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VI. Vendor Contact
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              手配先との連絡の型
            </h2>
            <div className="grid md:grid-cols-3 gap-0 mb-10 border-t-2 border-[#1a2942]">
              {vendorContact.map((r) => (
                <div key={r.en} className="border-b md:border-b-0 md:border-r last:border-r-0 border-[#e2ddd2] px-5 py-6">
                  <span className="block font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.12em] mb-2">{r.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{r.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{r.text}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#1a2942] p-8 sm:p-10">
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] max-w-3xl">
                手配先とのやりとりは、丁寧さだけでは足りません。誰が・いつ・何を・どこへ——を同じ順序で伝える型を持つことで、先方の聞き返しが減り、当日の齟齬が静かに小さくなります。個人の私有端末だけに履歴を残さず、家の当番が追える形で要点を残すことも、連絡の型に含めます。急な変更ほど、感情より最新の条件を先に置くことが信頼を保ちます。
              </p>
            </div>
          </section>

          {/* S7. 記録・引き継ぎ・休息＋FAQ — 統合セクション */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VII. Record, Handover & Rest
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              記録と引き継ぎ、そして自身の休息
            </h2>
            <div className="grid lg:grid-cols-[1fr_300px] gap-10 items-start mb-12">
              <div>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-6">
                  予定の記録は、美しい表を残すためではありません。次の当番が、未確定事項と確定事項を見誤らずに一日を受け取れるための共有材です。変更の履歴、手配先への最終連絡、家族と来客の重なりで残った注意点——これらを短い文で残し、口頭とメモの二重で渡します。
                </p>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-6">
                  引き継ぎのとき、評価や感想で埋めないことが肝要です。「混雑していた」ではなく「何時台にどの区間が遅れたか」、「来客が多かった」ではなく「何名がどの部屋を使ったか」。事実が残れば、翌日の先読みは一段正確になります。
                </p>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-6">
                  同時に、執事自身の休息も予定管理の対象です。判断力と観察は、睡眠と区切りのある非番に支えられます。奉仕を美談で続けて疲れを隠すことは、かえって依頼者の一日を危うくします。
                </p>
                <ul className="space-y-4">
                  {restGuides.map((line) => (
                    <li key={line} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.85]">
                      <span className="text-[#a38e65] shrink-0">—</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <img
                src="/images/generated/jba-std-schedule-06.jpg"
                alt="記録と休息のバランスを示し、引き継ぎを統括する上級執事"
                className="w-full h-56 lg:h-[320px] object-cover border border-[#a38e65]/30"
              />
            </div>
            <div className="border-t-2 border-[#1a2942]">
              <p className="font-['Playfair_Display',serif] uppercase tracking-[0.18em] text-[12px] text-[#8f7a56] pt-6 mb-4">
                Questions
              </p>
              {faqItems.map((item) => (
                <div key={item.q} className="border-b border-[#e2ddd2] py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3 flex gap-3">
                    <span className="text-[#a38e65] font-['Playfair_Display',serif] shrink-0">Q</span>
                    <span>{item.q}</span>
                  </h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] pl-8">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* S8. 関連導線 — 9th section, 8th h2 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VIII. Related Paths
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              関連する基準と学びへ
            </h2>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                時間・予定管理指針は、単独で完結するものではありません。行動規範で判断の节度を整え、ホスピタリティの流れのなかで場の連続を守り、資格制度を通じて段階的に再現性を育てることで、先読みと再構成の品位が続きます。全日本執事協会は、1890年（明治23年）から続く系譜のうえで、いまの暮らしにふさわしい奉仕の座標を示し続けます。
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
                <a href="/standards/conduct" className={BTN08_ON_NAVY}>
                  執事の行動規範
                </a>
                <a href="/standards/hospitality" className={BTN08_ON_NAVY}>
                  ホスピタリティの基準
                </a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>
                  資格制度を見る
                </a>
              </div>
              <div className="mt-6">
                <a href="/standards" className={BTN08_ON_LIGHT}>
                  基準・指針の一覧
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
